import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle2,
  ExternalLink,
  Eye,
  Loader2,
  Sparkles,
  RefreshCw,
  Wand2,
} from "lucide-react";
import { API } from "../Sidebar";

const GEO_API = `${API}/geo`;
const PREVIEW_URL = (path) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/geo/preview-by-path?path=${encodeURIComponent(path)}`;

const STATUS_LABEL = {
  queued: "Queued",
  outline: "Generating outline",
  sections: "Writing sections & FAQ",
  image: "Generating featured image",
  publishing: "Publishing to WordPress",
  done: "Done",
  error: "Failed",
};

export const BlogAIGenerate = ({ onPublished }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [activeTaskItem, setActiveTaskItem] = useState(null);
  const [task, setTask] = useState(null);

  const loadRoadmap = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${GEO_API}/blog/roadmap`);
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load roadmap");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoadmap();
  }, [loadRoadmap]);

  // Poll active task
  useEffect(() => {
    if (!activeTaskId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${GEO_API}/blog/status/${activeTaskId}`);
        if (cancelled) return;
        setTask(data);
        if (data.status === "done") {
          toast.success(`Published: ${data.result?.slug}`);
          setActiveTaskId(null);
          loadRoadmap();
          onPublished?.();
        } else if (data.status === "error") {
          toast.error(`Generation failed: ${data.message || data.error}`);
          setActiveTaskId(null);
        }
      } catch (err) {
        if (!cancelled) toast.error("Lost connection to task status");
      }
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [activeTaskId, loadRoadmap, onPublished]);

  const startGeneration = async (item) => {
    if (activeTaskId) {
      toast.error("A generation is already running — wait for it to finish.");
      return;
    }
    setActiveTaskItem(item);
    setTask(null);
    try {
      const payload = {
        topic: item.topic,
        primary_keyword: item.primary_keyword,
        category: item.category,
        internal_links: item.internal_links,
        publish_now: true,
        generate_image: true,
      };
      const { data } = await axios.post(`${GEO_API}/blog/ai-generate`, payload);
      setActiveTaskId(data.task_id);
      toast.success(`Started #${item.n} — ${item.topic.slice(0, 40)}…`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed to start");
      setActiveTaskItem(null);
    }
  };

  const groups = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      if (!map.has(it.week)) map.set(it.week, []);
      map.get(it.week).push(it);
    }
    return Array.from(map.entries());
  }, [items]);

  const done = items.filter((i) => i.is_published).length;

  return (
    <Card className="p-6 space-y-5" data-testid="blog-ai-generate-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-orange-500" /> Generate with AI
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            30-topic editorial roadmap. Each generation = ~11 GPT-5.2 calls + Nano Banana featured image
            (~2 min). Runs in background — you can close the tab and come back.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {done}/{items.length} published
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={loadRoadmap}
            disabled={loading}
            data-testid="blog-ai-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Active task banner */}
      {activeTaskId && task && (
        <Card
          className="p-4 bg-gradient-to-br from-orange-50 to-white border-orange-200"
          data-testid="blog-ai-active-task"
        >
          <div className="flex items-start gap-3">
            <Loader2 className="w-5 h-5 text-orange-500 animate-spin flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge className="bg-orange-500 hover:bg-orange-600 text-white text-xs">
                  {STATUS_LABEL[task.status] || task.status}
                </Badge>
                <span className="text-xs text-slate-500">
                  {task.progress || 0}% · #{activeTaskItem?.n} — {activeTaskItem?.topic}
                </span>
              </div>
              <Progress value={task.progress || 0} className="h-2 mt-2" />
              <p className="text-xs text-slate-600 mt-2">{task.message}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Result card (last completed task) */}
      {task?.status === "done" && task.result && (
        <Card
          className="p-4 bg-gradient-to-br from-emerald-50 to-white border-emerald-200"
          data-testid="blog-ai-result"
        >
          <div className="flex items-start gap-4">
            {task.result.featured_image && (
              <img
                src={task.result.featured_image}
                alt=""
                className="w-32 h-24 object-cover rounded-md border border-slate-200 flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <Badge className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Published
                </Badge>
                <span className="text-xs text-slate-500">
                  {task.result.word_count} words · {task.result.block_count} AI blocks
                </span>
              </div>
              <h4 className="font-semibold text-slate-900 truncate">{task.result.title}</h4>
              <p className="text-xs text-slate-500 font-mono mt-0.5 truncate">
                {task.result.preview_path}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <a
                  href={PREVIEW_URL(task.result.preview_path)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900 px-2.5 py-1 border border-slate-300 rounded-md hover:bg-slate-50"
                >
                  <Eye className="w-3 h-3" /> Preview render
                </a>
                {task.result.wp_link && (
                  <a
                    href={task.result.wp_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 px-2.5 py-1 border border-emerald-300 bg-white rounded-md hover:bg-emerald-50"
                  >
                    <ExternalLink className="w-3 h-3" /> View live
                  </a>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Grouped roadmap */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading roadmap…
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(([week, wkItems]) => (
            <div key={week} data-testid={`blog-ai-group-${week}`}>
              <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-slate-400" />
                {week}
                <span className="text-xs text-slate-400 font-normal">
                  ({wkItems.filter((x) => x.is_published).length}/{wkItems.length} published)
                </span>
              </h4>
              <div className="space-y-1.5">
                {wkItems.map((it) => (
                  <div
                    key={it.n}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-slate-200 bg-white hover:border-slate-300 transition-colors"
                    data-testid={`blog-ai-item-${it.n}`}
                  >
                    <span className="text-xs font-mono text-slate-400 w-6 text-right">
                      {String(it.n).padStart(2, "0")}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-800 truncate">
                          {it.topic}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {it.category}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        <span className="text-slate-400">keyword:</span>{" "}
                        <code className="font-mono">{it.primary_keyword}</code>
                      </p>
                    </div>
                    {it.is_published ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs border-emerald-200">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Published
                      </Badge>
                    ) : activeTaskItem?.n === it.n && activeTaskId ? (
                      <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-xs border-orange-200">
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Running
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startGeneration(it)}
                        disabled={!!activeTaskId}
                        className="text-xs h-7"
                        data-testid={`blog-ai-generate-btn-${it.n}`}
                      >
                        <Wand2 className="w-3 h-3 mr-1.5" /> Generate &amp; Publish
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default BlogAIGenerate;
